/**
 * useWlPlanMatrix — hooks para a Matriz de Planos no painel /wl.
 *
 * Faz tudo via RPCs SECURITY DEFINER:
 *   - wl_list_plan_matrix        → carrega planos + canais + provedores + vínculos
 *   - wl_upsert_plan_channel     → grava célula de canal × plano
 *   - wl_upsert_plan_provider    → grava célula de provider × plano
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlPlanMatrix");

// ============= Tipagens =============
export interface WlMatrixPlan {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export interface WlMatrixChannelType {
  id: string;
  slug: string;
  display_name: string;
  is_active: boolean;
  sort_order: number;
}

export interface WlMatrixProvider {
  id: string;
  // Slug intencionalmente removido — informação sigilosa do root admin.
  name: string;
  display_name: string | null;
  channel_type: string;
  is_active: boolean;
}


export interface WlMatrixPlanChannel {
  plan_id: string;
  channel_type_id: string;
  is_available: boolean;
  can_addon: boolean;
  addon_price_monthly: number | null;
  addon_setup_fee: number | null;
}

export interface WlMatrixPlanProvider {
  plan_id: string;
  provider_id: string;
  is_available: boolean;
  is_default: boolean;
  /** Elegível a consumir a cota de instâncias do plano (independente de padrão) */
  shares_plan_pool?: boolean;
}


export interface WlPlanMatrixPayload {
  plans: WlMatrixPlan[];
  channel_types: WlMatrixChannelType[];
  providers: WlMatrixProvider[];
  plan_channel_types: WlMatrixPlanChannel[];
  plan_providers: WlMatrixPlanProvider[];
}

export interface WlPlanMatrixFilters {
  search?: string | null;
  planIds?: string[] | null;
  includeInactive?: boolean;
}

// ============= Listagem =============
export function useWlPlanMatrix(filters: WlPlanMatrixFilters = {}) {
  const search = (filters.search || "").trim() || null;
  const planIds = filters.planIds && filters.planIds.length > 0 ? filters.planIds : null;
  const includeInactive = filters.includeInactive ?? true;

  return useQuery({
    queryKey: ["wl-plan-matrix", { search, planIds, includeInactive }],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WlPlanMatrixPayload> => {
      const { data, error } = await supabase.rpc("wl_list_plan_matrix" as never, {
        p_search: search,
        p_plan_ids: planIds,
        p_include_inactive: includeInactive,
      } as never);
      if (error) {
        log.error("Erro ao carregar matriz", { error });
        throw error;
      }
      const payload = (data || {}) as Partial<WlPlanMatrixPayload>;
      return {
        plans: payload.plans ?? [],
        channel_types: payload.channel_types ?? [],
        providers: payload.providers ?? [],
        plan_channel_types: payload.plan_channel_types ?? [],
        plan_providers: payload.plan_providers ?? [],
      };
    },
  });
}

// ============= Mutations =============
export function useWlPlanMatrixMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["wl-plan-matrix"] });

  const upsertChannel = useMutation({
    mutationFn: async (input: {
      plan_id: string;
      channel_type_id: string;
      is_available: boolean;
      can_addon: boolean;
      addon_price_monthly: number | null;
      addon_setup_fee: number | null;
    }) => {
      const { error } = await supabase.rpc("wl_upsert_plan_channel" as never, {
        p_plan_id: input.plan_id,
        p_channel_type_id: input.channel_type_id,
        p_is_available: input.is_available,
        p_can_addon: input.can_addon,
        p_addon_price_monthly: input.addon_price_monthly,
        p_addon_setup_fee: input.addon_setup_fee,
      } as never);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const upsertProvider = useMutation({
    mutationFn: async (input: {
      plan_id: string;
      provider_id: string;
      is_available: boolean;
      is_default: boolean;
      /** null/undefined preserva o valor atual no banco */
      shares_plan_pool?: boolean | null;
    }) => {
      const { error } = await supabase.rpc("wl_upsert_plan_provider" as never, {
        p_plan_id: input.plan_id,
        p_provider_id: input.provider_id,
        p_is_available: input.is_available,
        p_is_default: input.is_default,
        p_shares_plan_pool: input.shares_plan_pool ?? null,
      } as never);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });


  return { upsertChannel, upsertProvider };
}
